import React, { useState, useEffect, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import "../styles/components/form.css";
import "../styles/components/promotion_form.css";
import DeleteIcon from "../assets/icons/delete-white-icon.svg";
import CloseIcon from "../assets/icons/close-icon.svg";
import WinnerItem from "../components/WinnerItem.jsx";
import WinnerForm from "../components/WinnerForm.jsx";
import Modal from "../components/Modal.jsx";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createPromotion, updatePromotion } from "../services/promotionsService";

import { IMAGE_BASE_URL } from "../services/imageConfig";

const getLocalDateString = () => {
    const now = new Date();
    const localISO = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
    return localISO.slice(0, 10);
};

const getTomorrowLocalDateString = () => {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 86400000);
    const localISO = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString();
    return localISO.slice(0, 10);
};

const urlToFile = async (url, filename = "image.jpg") => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
};

const PromotionForm = ({ onClose, initialData, mode, onSuccess }) => {
    const initialDetailCount = (() => {
        let count = 0;
        if (initialData && initialData.imageFirst) count++;
        if (initialData && initialData.imageSecond) count++;
        return count;
    })();

    let initialStatus = true;
    if (initialData && typeof initialData.promotionStatus !== "undefined") {
        initialStatus =
            typeof initialData.promotionStatus === "boolean"
                ? initialData.promotionStatus
                : initialData.promotionStatus === "true";
    }

    const [promotionTitle, setPromotionTitle] = useState((initialData && initialData.title) || "");
    const [promotionDescription, setPromotionDescription] = useState((initialData && initialData.description) || "");
    const [promotionDate, setPromotionDate] = useState((initialData && initialData.date) || getLocalDateString());
    const [promotionDateOfExpiry, setPromotionDateOfExpiry] = useState(
        (initialData && initialData.dateOfExpiry) || getTomorrowLocalDateString()
    );
    const [promotionConditions, setPromotionConditions] = useState((initialData && initialData.promotionDetails) || "");

    const [sliderImage, setSliderImage] = useState(initialData ? initialData.titleImage : null);
    const [isOngoing, setIsOngoing] = useState(initialData ? initialStatus : true);
    const [winners, setWinners] = useState((initialData && initialData.promotionWinners) || []);
    const [promotionDetailImages, setPromotionDetailImages] = useState(
        initialData && initialData.imageFirst ? [initialData.imageFirst, initialData.imageSecond].filter(Boolean) : []
    );

    const sliderInputRef = useRef(null);
    const detailInputRef = useRef(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const MAX_FILE_SIZE_MB = 10;

    const [sliderPreviewUrl, setSliderPreviewUrl] = useState(null);
    useEffect(() => {
        if (!sliderImage) {
            setSliderPreviewUrl(null);
            return;
        }
        if (sliderImage instanceof File) {
            const objectUrl = URL.createObjectURL(sliderImage);
            setSliderPreviewUrl(objectUrl);
            return () => {
                URL.revokeObjectURL(objectUrl);
            };
        }
        if (typeof sliderImage === "string") {
            setSliderPreviewUrl(
                sliderImage.startsWith("http") ? sliderImage : IMAGE_BASE_URL + sliderImage
            );
        }
    }, [sliderImage]);

    const [detailPreviews, setDetailPreviews] = useState([]);
    useEffect(() => {
        if (!promotionDetailImages || promotionDetailImages.length === 0) {
            setDetailPreviews([]);
            return;
        }
        const newUrls = promotionDetailImages.map((file) => {
            if (file instanceof File) {
                return URL.createObjectURL(file);
            }
            if (typeof file === "string") {
                return file.startsWith("http") ? file : IMAGE_BASE_URL + file;
            }
            return null;
        });
        setDetailPreviews(newUrls);
        return () => {
            newUrls.forEach((url) => {
                if (url && url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [promotionDetailImages]);

    const isStatusLocked = mode === "edit" && initialData && !initialStatus;

    const [showWinnerForm, setShowWinnerForm] = useState(false);
    const [currentWinnerData, setCurrentWinnerData] = useState(null);
    const [winnerFormMode, setWinnerFormMode] = useState("create");

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [winnerToDelete, setWinnerToDelete] = useState(null);

    const handleAddWinner = () => {
        setCurrentWinnerData(null);
        setWinnerFormMode("create");
        setShowWinnerForm(true);
    };

    const handleEditWinner = (winner) => {
        setCurrentWinnerData(winner);
        setWinnerFormMode("edit");
        setShowWinnerForm(true);
    };

    const handleConfirmDelete = () => {
        setWinners((prev) => prev.filter((winner) => winner.id !== winnerToDelete));
        setShowDeleteModal(false);
    };

    const handleSaveWinner = (winnerData) => {
        if (winnerFormMode === "create") {
            const newWinner = { ...winnerData, id: Date.now() };
            setWinners((prev) => [...prev, newWinner]);
        } else {
            setWinners((prev) =>
                prev.map((winner) => (winner.id === winnerData.id ? winnerData : winner))
            );
        }
        setShowWinnerForm(false);
    };

    const handleCloseWinnerForm = () => {
        setShowWinnerForm(false);
    };

    const isValidFileSize = (file) => {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            toast.error(`Файл "${file.name}" превышает максимальный размер 10MB.`, {
                position: "bottom-left",
                autoClose: 3000,
            });
            return false;
        }
        return true;
    };

    const handleSliderFileChange = (e) => {
        const file = e.target.files[0];
        if (file && isValidFileSize(file)) setSliderImage(file);
    };

    const handleSliderDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && isValidFileSize(file)) setSliderImage(file);
    };

    const handleSliderClick = () => {
        sliderInputRef.current?.click();
    };

    const removeSliderImage = () => {
        setSliderImage(null);
    };

    const handleDateChange = (e) => {
        setPromotionDate(e.target.value);
    };

    const handleDateOfExpiryChange = (e) => {
        setPromotionDateOfExpiry(e.target.value);
    };

    const handleDetailFileChange = (e) => {
        const files = Array.from(e.target.files);
        let newFiles = [...promotionDetailImages];

        for (const file of files) {
            if (newFiles.length >= 2) break;
            if (isValidFileSize(file)) {
                newFiles.push(file);
            }
        }

        setPromotionDetailImages(newFiles);
    };

    const handleDetailDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        let newFiles = [...promotionDetailImages];

        for (const file of files) {
            if (newFiles.length >= 2) break;
            if (isValidFileSize(file)) {
                newFiles.push(file);
            }
        }

        setPromotionDetailImages(newFiles);
    };

    const handleDetailClick = () => {
        detailInputRef.current?.click();
    };

    const removeDetailImage = (index) => {
        setPromotionDetailImages((prev) => prev.filter((_, i) => i !== index));
    };

    const isFormValid = () => {
        if (
            !promotionTitle.trim() ||
            !promotionDescription.trim() ||
            !promotionDate.trim() ||
            !promotionDateOfExpiry.trim()
        ) {
            return false;
        }
        if (!sliderImage) return false;
        if (isOngoing) {
            if (!promotionConditions.trim()) {
                return false;
            }
        } else {
            if (!winners || winners.length === 0) {
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) return;

        setIsSubmitting(true);

        const data_client = {
            title: promotionTitle,
            description: promotionDescription,
            date: promotionDate,
            dateOfExpiry: promotionDateOfExpiry,
            promotionDetails: isOngoing ? promotionConditions : "",
            promotionStatus: isOngoing,
            promotionWinners: isOngoing ? [] : winners.map(({ id, ...rest }) => rest),
        };

        const formData = new FormData();
        formData.append("data_client", JSON.stringify(data_client));

        if (sliderImage instanceof File) {
            formData.append("image_article", sliderImage);
        } else {
            formData.append("image_article", "null");
        }

        let image_first = "null";
        let image_second = "null";
        let isDeleted = false;

        const finalCount = promotionDetailImages.length;
        if (finalCount > 0) {
            image_first = promotionDetailImages[0] instanceof File
                ? promotionDetailImages[0]
                : await urlToFile(promotionDetailImages[0]);

            if (finalCount > 1) {
                image_second = promotionDetailImages[1] instanceof File
                    ? promotionDetailImages[1]
                    : await urlToFile(promotionDetailImages[1]);
                isDeleted = false;
            } else {
                image_second = "null";
                isDeleted = initialDetailCount === 2;
            }
        } else {
            image_first = "null";
            image_second = "null";
            isDeleted = initialDetailCount >= 1;
        }

        formData.append("image_first", image_first);
        formData.append("image_second", image_second);
        if (mode === "edit") {
            formData.append("isDeleted", isDeleted ? "true" : "false");
        }

        try {
            if (mode === "create") {
                await createPromotion(formData);
                toast.success("Акция успешно создана", {
                    position: "bottom-left",
                    autoClose: 3000,
                });
            } else {
                await updatePromotion(initialData.id, formData);
                toast.success("Изменения акции сохранены", {
                    position: "bottom-left",
                    autoClose: 3000,
                });
            }
            onSuccess?.();
            onClose();
        } catch (error) {
            toast.error(`Ошибка: ${error.response?.data?.message || error.message}`, {
                position: "bottom-left",
                autoClose: 3000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleStatusChange = (value) => {
        if (!isStatusLocked) {
            setIsOngoing(value);
        }
    };

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    return (
        <div className="modal-overlay">
            <div className="form">
                <div className="form-header">
                    <h1 className="form-title">
                        {mode === "edit" ? "Редактирование акции" : "Создание акции"}
                    </h1>
                    <button className="close-button" onClick={onClose}>
                        <img src={CloseIcon} alt="Закрыть" />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <h2 className="form-section-title">Информация акции для слайдера</h2>
                    <div className="form-group">
                        <label className="field-label">Картинка для слайдера акции</label>
                        <div
                            className={`drag-drop-area ${isStatusLocked ? 'disabled' : ''}`}
                            onClick={!isStatusLocked ? handleSliderClick : undefined}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={!isStatusLocked ? handleSliderDrop : undefined}
                            style={{ pointerEvents: isStatusLocked ? 'none' : 'auto', opacity: isStatusLocked ? 0.5 : 1 }}
                        >

                            {sliderPreviewUrl ? (
                                <div className="image-preview">
                                    <img
                                        src={sliderPreviewUrl}
                                        alt="Превью слайдера"
                                        className="preview-image"
                                    />
                                    <button
                                        type="button"
                                        className="delete-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSliderImage();
                                        }}
                                    >
                                        <img src={DeleteIcon} alt="Удалить" />
                                    </button>
                                </div>
                            ) : (
                                <span className="placeholder-text">
                                    Перетащите или кликните для загрузки картинки
                                </span>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                ref={sliderInputRef}
                                onChange={handleSliderFileChange}
                                style={{ display: "none" }}
                            />
                        </div>
                        <label className="field-label max-size-label">Максимальный размер для изображения: 10MB</label>
                    </div>
                    <div className="form-group">
                        <label className="field-label">Дата акции</label>
                        <input
                            type="date"
                            className="date-input"
                            value={promotionDate}
                            onChange={handleDateChange}
                        />
                    </div>
                    <div className="form-group">
                        <label className="field-label">Дата окончания акции</label>
                        <input
                            type="date"
                            className="date-input"
                            value={promotionDateOfExpiry}
                            onChange={handleDateOfExpiryChange}
                        />
                    </div>
                    <div className="form-group">
                        <label className="field-label">Заголовок акции</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите заголовок акции"
                            value={promotionTitle}
                            onChange={(e) => setPromotionTitle(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label className="field-label">Описание акции</label>
                        <TextareaAutosize
                            className="textarea-input"
                            placeholder="Введите описание акции"
                            value={promotionDescription}
                            onChange={(e) => setPromotionDescription(e.target.value)}
                        />
                    </div>
                    <div className="form-group promotion-status-group">
                        <label className="field-label">Статус акции</label>
                        <div className="radio-buttons">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="promotionStatus"
                                    value="ongoing"
                                    checked={isOngoing === true}
                                    onChange={() => handleStatusChange(true)}
                                    disabled={isStatusLocked}
                                />
                                Акция продолжается
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="promotionStatus"
                                    value="finished"
                                    checked={isOngoing === false}
                                    onChange={() => handleStatusChange(false)}
                                    disabled={isStatusLocked}
                                />
                                Акция завершена
                            </label>
                        </div>
                    </div>
                    {isOngoing ? (
                        <>
                            <h2 className="form-section-title">Подробная информация акции</h2>
                            <div className="form-group">
                                <label className="field-label">
                                    Картинки для условий акции (макс 2)
                                </label>
                                <div
                                    className="drag-drop-area"
                                    onClick={handleDetailClick}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleDetailDrop}
                                >
                                    {detailPreviews.length > 0 ? (
                                        <div className="detail-images-preview">
                                            {detailPreviews.map((url, index) => (
                                                <div key={index} className="image-preview">
                                                    <img
                                                        src={url}
                                                        alt={`Превью условия ${index + 1}`}
                                                        className="preview-image"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="delete-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeDetailImage(index);
                                                        }}
                                                    >
                                                        <img src={DeleteIcon} alt="Удалить" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="placeholder-text">
                                            Перетащите или кликните для загрузки картинок
                                        </span>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        ref={detailInputRef}
                                        onChange={handleDetailFileChange}
                                        style={{ display: "none" }}
                                        max={2}
                                    />
                                </div>
                                <label className="field-label max-size-label">Максимальный размер для каждого изображения: 10MB</label>
                            </div>
                            <div className="form-group">
                                <label className="field-label">Условия акции</label>
                                <TextareaAutosize
                                    className="textarea-input description"
                                    placeholder="Введите условия акции"
                                    value={promotionConditions}
                                    onChange={(e) => setPromotionConditions(e.target.value)}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="form-section-title">Список победителей</h2>
                            <div className="form-items-container">
                                {winners && winners.length > 0 ? (
                                    winners.map((winner, index) => (
                                        <WinnerItem
                                            key={winner.id}
                                            winner={winner}
                                            index={index + 1}
                                            onEdit={() => handleEditWinner(winner)}
                                            onDelete={(id) => {
                                                setWinnerToDelete(id);
                                                setShowDeleteModal(true);
                                            }}
                                        />
                                    ))
                                ) : (
                                    <span className="empty-list-label">Список победителей пуст</span>
                                )}
                                <button
                                    type="button"
                                    className="add-to-list-button"
                                    onClick={handleAddWinner}
                                >
                                    + Добавить победителя
                                </button>
                            </div>
                        </>
                    )}
                    <div className="form-buttons">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={onClose}
                        >
                            Отменить
                        </button>
                        <button
                            type="submit"
                            className="save-button"
                            disabled={!isFormValid()}
                        >
                            Сохранить
                        </button>
                    </div>
                </form>
                {isSubmitting && (
                    <div className="loader-overlay">
                        <Loader />
                        <p>Сохранение акции...</p>
                    </div>
                )}
            </div>

            {showDeleteModal && (
                <Modal
                    isOpen={showDeleteModal}
                    message="Вы действительно хотите удалить победителя"
                    itemTitle={winners.find((w) => w.id === winnerToDelete)?.fullName}
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={handleConfirmDelete}
                />
            )}

            {showWinnerForm && (
                <WinnerForm
                    onClose={handleCloseWinnerForm}
                    initialData={currentWinnerData}
                    mode={winnerFormMode}
                    onSave={handleSaveWinner}
                />
            )}
        </div>
    );
};

export default PromotionForm;
